import {
  ArrowRightStartOnRectangleIcon,
  EllipsisVerticalIcon,
  LanguageIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
} from "@heroicons/react/16/solid";
import {
  BellIcon,
  ChatBubbleLeftEllipsisIcon,
  InformationCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { SupportedLanguage } from "@tumtutor/shared/type";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";
import i18n from "../../../i18n";
import { useActiveNotificationsQuery } from "../../services/notificationApi";
import {
  useIsUserManagingAnyCourse,
  useIsUserRole,
  useLogout,
} from "../../utils/auth";
import { FeedBackForm } from "../FeedBackForm";
import BackButton from "../backButton";
import Modal, { ModalProps, openModal } from "../modal";
import { DynamicLogo } from "./DynamicLogo";
import Notifications from "./Notifications";
import ThemeSelector from "./ThemeSelector";

interface HeaderProp {
  children: ReactNode;
  breadcrumbs?: BreadcrumbElement[];
  bottomPadding?: boolean;
  infoModal?: ModalProps;
  showFooter?: boolean;
  scrollable?: boolean;
  backButtonRoute?: string;
}

export interface BreadcrumbElement {
  name: string;
  link?: string;
}

function Header({
  children,
  breadcrumbs,
  bottomPadding = true,
  infoModal,
  showFooter,
  scrollable = true,
  backButtonRoute,
}: HeaderProp) {
  const navigate = useNavigate();
  const auth = useAuth();
  const isAdmin = useIsUserRole("admin");
  const isManagingCourse = useIsUserManagingAnyCourse();
  const { t } = useTranslation(["header"]);
  const logout = useLogout();
  const feedbackForumURL = "https://groups.google.com/a/onetutor.ai/g/feedback";

  const handleHomeClick = () => {
    navigate("/");
  };

  const handlePrivacyClick = () => {
    navigate("/privacy");
  };

  const handleImprintClick = () => {
    navigate("/imprint");
  };

  const { data: notifications } = useActiveNotificationsQuery(undefined, {
    skip: !auth.isAuthenticated,
  });

  return (
    <div
      className={`drawer drawer-end flex flex-col ${
        scrollable ? "min-h-screen" : "h-[100dvh]"
      }`}
    >
      <input
        id="notifications-drawer"
        type="checkbox"
        className="drawer-toggle"
      />
      <div className="drawer-content z-0 flex h-full min-h-0 flex-1 flex-col">
        <div className="navbar bg-base-100">
          <div className="flex flex-1 items-center justify-between">
            <button
              onClick={handleHomeClick}
              className="btn btn-ghost no-animation flex items-center justify-center gap-0 px-1 md:px-4"
              type="button"
            >
              <DynamicLogo />
            </button>
            <div className="flex items-center gap-4">
              {auth.user && (
                <label
                  htmlFor="notifications-drawer"
                  className="drawer-button btn btn-ghost btn-sm btn-circle indicator"
                >
                  <BellIcon className="h-5 w-5" />
                  {notifications && notifications.length > 0 && (
                    <span className="badge badge-sm badge-error indicator-item">
                      {notifications.length}
                    </span>
                  )}
                </label>
              )}
              <ThemeSelector />
              {isAdmin && (
                <button
                  onClick={() => navigate("/admin/users")}
                  className="btn btn-ghost btn-sm btn-circle"
                  type="button"
                >
                  <UserIcon className="h-5 w-5" />
                </button>
              )}
              <button
                disabled
                type="button"
                className="badge badge-lg border-2 border-neutral-content font-semibold"
              >
                Beta
              </button>
            </div>
          </div>
          <div className="ml-2 flex-none gap-5">
            <div className="dropdown dropdown-hover dropdown-end">
              <div className="rounded-full">
                <button
                  tabIndex={0}
                  type="button"
                  className="btn btn-ghost btn-sm btn-circle avatar"
                >
                  <div className="rounded-full">
                    <EllipsisVerticalIcon className="h-5 w-5" />
                  </div>
                </button>
              </div>
              <ul className="dropdown-content menu z-[1] w-64 rounded-box bg-neutral p-2 shadow">
                <li>
                  <button
                    tabIndex={0}
                    onClick={() => {
                      if (i18n.language === SupportedLanguage.DE) {
                        i18n.changeLanguage(SupportedLanguage.EN);
                      } else if (i18n.language === SupportedLanguage.EN) {
                        i18n.changeLanguage(SupportedLanguage.DE);
                      }
                    }}
                    type="button"
                  >
                    <LanguageIcon className="h-5 w-5" />
                    <span>
                      {i18n.language === SupportedLanguage.DE
                        ? "English"
                        : "Deutsch"}
                    </span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    tabIndex={0}
                    onClick={() => openModal("feedbackFormModal")}
                  >
                    <ChatBubbleLeftEllipsisIcon className="h-5 w-5" />
                    <span>Feedback</span>
                  </button>
                </li>
                {isManagingCourse && (
                  <>
                    <li>
                      <button
                        type="button"
                        tabIndex={0}
                        onClick={() =>
                          window.open(
                            t("docURL"),
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                      >
                        <QuestionMarkCircleIcon className="h-5 w-5" />
                        <span>{t("documentation")}</span>
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        tabIndex={0}
                        onClick={() =>
                          window.open(
                            feedbackForumURL,
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                      >
                        <UserGroupIcon className="h-5 w-5" />
                        <span>{t("feedbackForumURL")}</span>
                      </button>
                    </li>
                  </>
                )}
                {auth.user && (
                  <li>
                    <button tabIndex={0} onClick={logout} type="button">
                      <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
                      {t("logout")}
                    </button>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
        <div
          className={`mx-4 flex min-h-0 flex-grow flex-col md:mx-10 ${
            bottomPadding ? "mb-6" : ""
          }`}
        >
          {breadcrumbs ? (
            <div className="flex items-center justify-between">
              <div className="breadcrumbs overflow-x-scroll p-0">
                <ul className="flex">
                  <li>
                    <button
                      className="link link-hover text-sm sm:text-base"
                      onClick={() => navigate("/")}
                      type="button"
                    >
                      {t("startPage")}
                    </button>
                  </li>
                  {breadcrumbs.map((element) => (
                    <li key={element.name}>
                      {element?.link ? (
                        <button
                          className="link link-hover text-sm sm:text-base"
                          onClick={() => navigate(element?.link ?? "")}
                          type="button"
                        >
                          {element.name}
                        </button>
                      ) : (
                        <span className="text-sm sm:text-base">
                          {element.name}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center justify-end">
                {infoModal && (
                  <>
                    <button
                      className="btn btn-sm btn-ghost btn-circle mr-2"
                      type="button"
                      onClick={() => infoModal && openModal(infoModal.id)}
                    >
                      <InformationCircleIcon className="h-6 w-6" />
                    </button>
                    <Modal {...infoModal} />
                  </>
                )}
                <div className="mr-sm hidden sm:block">
                  <BackButton route={backButtonRoute} />
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6" />
          )}
          <div className="min-h-0 flex-1">{children}</div>
        </div>

        {showFooter && (
          <div className="mb-10 flex flex-col items-center">
            <div className="flex justify-center gap-x-10">
              <button onClick={handlePrivacyClick} type="button">
                {t("dataPrivacy")}
              </button>
              <button onClick={handleImprintClick} type="button">
                {t("imprint")}
              </button>
              {isManagingCourse && (
                <a
                  href={t("docURL")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link link-hover"
                >
                  {t("documentation")}
                </a>
              )}
            </div>
            <span className="mt-1 text-sm italic opacity-50">
              {t("awsSupport")}
            </span>
          </div>
        )}
      </div>
      <FeedBackForm />
      <Notifications />
    </div>
  );
}

export default Header;
